import type { FC, ReactNode } from 'react';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import type { ITopWin, IWinAmount } from '../../types/topWins.types';
import { splitAmount } from '../../utils/formatAmount';
import styles from './TopWinAmount.module.css';

interface TopWinAmountProps {
  win: ITopWin;
  /** Optional extra class merged onto the wrapper (e.g. for layout tweaks). */
  className?: string;
}

const AmountLine: FC<{ amount: IWinAmount }> = ({ amount }) => {
  const { integer, decimals } = splitAmount(amount.amount);
  const rowRef = useRef<HTMLParagraphElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  // Shrink-to-fit: scale the text down (never up) so a long amount fits its row
  // instead of overflowing/ellipsising. We measure `scrollWidth`, which is
  // layout-based and unaffected by the applied transform — so reading it back
  // never feeds into the scale (no shrink loop, the bug AutoTextfit had here).
  useLayoutEffect(() => {
    const row = rowRef.current;
    const text = textRef.current;
    if (!row || !text) return;

    const fit = () => {
      const natural = text.scrollWidth;
      // Reserve a horizontal breathing margin (--tw-amount-inset px per side) so
      // a scaled-to-fit amount doesn't sit edge-to-edge in the card.
      const inset = parseFloat(getComputedStyle(row).getPropertyValue('--tw-amount-inset')) || 0;
      const available = row.clientWidth - inset * 2;
      // Layout not ready yet (e.g. card mounted while its view was hidden): the
      // resize observer below fits it once the row gets a size. (This used to
      // retry every animation frame, which forced a layout per frame for as long
      // as the view stayed hidden - all of an All Games session.)
      if (!natural || available <= 0) return;
      setScale(Math.min(1, available / natural));
    };

    fit();
    // Re-fit when the row resizes (viewport/breakpoint, or shown after being
    // hidden) or the text's intrinsic size changes (font-size swap at a
    // breakpoint). Transform changes don't alter layout size, so observing
    // these can't loop.
    const ro = new ResizeObserver(fit);
    ro.observe(row);
    ro.observe(text);
    return () => {
      ro.disconnect();
    };
  }, [integer, decimals, amount.currency]);

  return (
    <p ref={rowRef} className={styles.row}>
      <span
        ref={textRef}
        className={styles.fit}
        style={scale < 1 ? { transform: `scale(${scale})` } : undefined}
      >
        <span className={styles.major}>
          {integer}.{decimals}
        </span>
        <span className={styles.currency}>{amount.currency}</span>
      </span>
    </p>
  );
};

// Converted ↔ original swap cycle (ms): hold the first line, slide to the second, hold it, slide back.
const SWAP_CYCLE_MS = 6000;
const SWAP_DOWN_AT_MS = 2700;
const SWAP_BACK_AT_MS = 5700;

/**
 * The two-line reel. The moves are CSS transitions on `transform` (TopWinAmount.module.css) toggled from a timer, so nothing
 * runs between them; as one infinite keyframe animation, Chromium restyled, laid out and repainted every reel on every
 * frame, holds included - three quarters of the lobby's main-thread time while idle. The cycle keeps the animation's
 * timing rules: it starts when the reel gets a box (mount, or shown again after display:none - which cancelled the
 * animation) and starts over from the first line each time. With transitions switched off (reduced motion, frozen
 * Storybook snapshots) the reel stays on the first line, as it did with the animation switched off.
 */
const AmountReel: FC<{ children: ReactNode }> = ({ children }) => {
  const reelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const reel = reelRef.current;
    if (!reel) return undefined;

    let start = 0;
    let timer = 0;
    let running = false;
    const step = () => {
      const t = (performance.now() - start) % SWAP_CYCLE_MS;
      const swapped = t >= SWAP_DOWN_AT_MS && t < SWAP_BACK_AT_MS;
      if (reel.hasAttribute('data-swapped') !== swapped) {
        reel.setAttribute('data-moving', '');
        const moves = getComputedStyle(reel).transitionDuration !== '0s';
        if (!moves) reel.removeAttribute('data-moving');
        reel.toggleAttribute('data-swapped', swapped && moves);
      }
      const next =
        t < SWAP_DOWN_AT_MS ? SWAP_DOWN_AT_MS - t : swapped ? SWAP_BACK_AT_MS - t : SWAP_CYCLE_MS - t + SWAP_DOWN_AT_MS;
      timer = window.setTimeout(step, next);
    };
    // The first line again, and the cycle from its start: when the reel gets a box, and when reduced motion is
    // switched (switching the animation on or off restarted it).
    const restart = () => {
      window.clearTimeout(timer);
      reel.removeAttribute('data-swapped');
      reel.removeAttribute('data-moving');
      if (!running) return;
      start = performance.now();
      timer = window.setTimeout(step, SWAP_DOWN_AT_MS);
    };
    const observer = new ResizeObserver(([entry]) => {
      const shown = !!entry && (entry.contentRect.width > 0 || entry.contentRect.height > 0);
      if (shown === running) return;
      running = shown;
      restart();
    });
    const moveDone = (event: TransitionEvent) => {
      if (event.target === reel) reel.removeAttribute('data-moving');
    };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    observer.observe(reel);
    reel.addEventListener('transitionend', moveDone);
    reel.addEventListener('transitioncancel', moveDone);
    reducedMotion.addEventListener('change', restart);
    return () => {
      observer.disconnect();
      reel.removeEventListener('transitionend', moveDone);
      reel.removeEventListener('transitioncancel', moveDone);
      reducedMotion.removeEventListener('change', restart);
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div ref={reelRef} className={styles.reel}>
      {children}
    </div>
  );
};

/**
 * Shared Top Wins amount display, used by both the small and featured cards.
 *
 * When the win was converted to the lobby's display currency it shows both
 * values in a vertically-swapping reel; otherwise a single static line. A value
 * too wide for its row is scaled down to fit (never overflows or ellipsises).
 *
 * Size, colour, alignment and vertical nudge are themed by the parent via the
 * `--tw-amount-*` custom properties — see TopWinAmount.module.css.
 */
const TopWinAmount: FC<TopWinAmountProps> = ({ win, className }) => {
  const primary = win.converted ?? win.original;
  // Only when a conversion actually happened do we show the original alongside
  // the converted amount (swapping reel); otherwise a single static line.
  const secondary = win.converted ? win.original : null;

  return (
    <div className={className ? `${styles.wrapper} ${className}` : styles.wrapper}>
      {secondary ? (
        <AmountReel>
          <AmountLine amount={primary} />
          <AmountLine amount={secondary} />
        </AmountReel>
      ) : (
        <AmountLine amount={primary} />
      )}
    </div>
  );
};

export default memo(TopWinAmount);
